class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  static get observedAttributes() {
    return ['data-slides', 'data-slides-mobile', 'data-space', 'data-space-mobile', 'data-speed', 'data-arrows', 'data-pagination', 'data-scrollbar', 'data-mousewheel', 'data-free-mode'];
  }

  attributeChangedCallback() {
    if (this.isConnected) {
      this.init(true);
    }
  }

  get opts() {
    const parseBool = v => String(v).toLowerCase() === 'true';
    const swiperContainer = this;
    
    const baseSlides = Number(this.getAttribute('data-slides')) || 1;
    const baseSlidesMobile = Number(this.getAttribute('data-slides-mobile')) || 1;
    
    const config = {
      slidesPerView: baseSlides,
      spaceBetween: Number(this.getAttribute('data-space')) || 0,
      speed: Number(this.getAttribute('data-speed')) || 300,
      grabCursor: true,
      touchRatio: 1,
      touchAngle: 45,
      simulateTouch: true,
      allowTouchMove: true,
      breakpoints: {
        0: {
          slidesPerView: baseSlidesMobile,
          spaceBetween: Number(this.getAttribute('data-space-mobile')) || 0,
        },
        950: {
          slidesPerView: baseSlides,
          spaceBetween: Number(this.getAttribute('data-space')) || 0,
        }
      }
    };

    if (parseBool(this.getAttribute('data-arrows')) && swiperContainer) {
      const nextEl = swiperContainer.querySelector('.swiper-button-next');
      const prevEl = swiperContainer.querySelector('.swiper-button-prev');
      if (nextEl && prevEl) {
        config.navigation = {
          nextEl: nextEl,
          prevEl: prevEl
        };
      }
    }

    if (parseBool(this.getAttribute('data-pagination')) && swiperContainer) {
      const paginationEl = swiperContainer.querySelector('.swiper-pagination');
      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: true
        };
      }
    }

    if (parseBool(this.getAttribute('data-scrollbar')) && swiperContainer) {
      const scrollbarEl = swiperContainer.querySelector('.swiper-scrollbar');
      if (scrollbarEl) {
        config.scrollbar = {
          el: scrollbarEl,
          draggable: true
        };
      }
    }

    if (parseBool(this.getAttribute('data-mousewheel'))) {
      config.mousewheel = {
        enabled: true,
        forceToAxis: true,
        sensitivity: 1
      };
    }

    if (parseBool(this.getAttribute('data-free-mode'))) {
      config.freeMode = {
        enabled: true,
        sticky: false,
        momentum: true,
        momentumRatio: 1,
        momentumVelocityRatio: 1
      };
    }

    return config;
  }

  init(reinit = false) {
    const swiperContainer = this.querySelector('.swiper');
    if (!swiperContainer || !swiperContainer.querySelector('.swiper-wrapper')) return;

    if (this._swiper || reinit) {
      this.destroy();
    }

    const opts = this.opts;
    
    try {
      if (typeof Swiper === 'undefined') {
        console.error('Swiper library not loaded');
        return;
      }
      this._swiper = new Swiper(swiperContainer, opts);

    } catch (error) {
      console.error('Swiper initialization failed:', error);
    }
  }

  destroy() {
    if (this._swiper && this._swiper.destroy) {
      this._swiper.destroy(true, true);
      this._swiper = null;
    }
  }
}
customElements.define('swiper-slider', SwiperSlider);

document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('swiper-slider').forEach(el => {
    if (el.init) el.init(true);
  });
});
document.addEventListener('shopify:section:unload', e => {
  e.target.querySelectorAll && e.target.querySelectorAll('swiper-slider').forEach(el => el.destroy && el.destroy());
});



class ScrollingGallery extends HTMLElement {
  connectedCallback() {
    this.initSwiper();
    this.addEventListener('shopify:block:select', () => this.swiper?.slideTo(0));
  }

  initSwiper() {
    const container = this.querySelector('.swiper');
    if (!container || typeof Swiper === 'undefined') return;

    const slidesDesktop = parseFloat(container.dataset.slidesPerView) || 3.5;
    const slidesMobile = parseFloat(container.dataset.slidesPerViewMobile) || 1.5;
    const spaceBetween = Number(container.dataset.spaceBetween) || 8;
    const spaceBetweenMobile = Number(container.dataset.spaceBetweenMobile) || 8;
    const autoplayEnabled = String(container.dataset.autoplay).toLowerCase() === 'true';
    const autoplayContinuous = String(container.dataset.autoplayContinuous).toLowerCase() === 'true';
    const autoplayDelay = Number(container.dataset.autoplayDelay) || 5000;
    const autoplaySpeed = Number(container.dataset.autoplaySpeed) || 12000;
    const autoplayPauseOnHover = String(container.dataset.autoplayPause).toLowerCase() === 'true';

    const config = {
      speed: 600,
      parallax: true,
      mousewheel: { 
        releaseOnEdges: true,
        forceToAxis: false,
        sensitivity: 0.7,
        thresholdDelta: 2
      },
      freeMode: true,
      slidesPerView: slidesMobile,
      spaceBetween: spaceBetweenMobile,
      breakpoints: {
        990: { 
          slidesPerView: slidesDesktop, 
          spaceBetween: spaceBetween,
        }
      }
    };

    if (autoplayEnabled) {
      if (autoplayContinuous) {
        config.loop = true;
        config.speed = Math.max(2000, autoplaySpeed);
        config.freeMode = true;
        config.freeModeMomentum = false;
        config.autoplay = {
          delay: 0,
          disableOnInteraction: false,
          pauseOnMouseEnter: autoplayPauseOnHover
        };
      } else {
        config.autoplay = {
          delay: Math.max(1000, autoplayDelay),
          disableOnInteraction: false,
          pauseOnMouseEnter: autoplayPauseOnHover
        };
      }
    }

    this.swiper = new Swiper(container, config);
    this.setupLoopWheelRelease(container);
  }

  setupLoopWheelRelease(container) {
    if (!this.swiper?.params?.loop || !this.swiper?.params?.mousewheel) return;

    const loopDistance = this.getLoopDistance();
    if (!loopDistance) return;

    const state = {
      active: false,
      released: false,
      distance: 0,
      prevTranslate: null,
      resetTimer: null,
      releaseTimer: null
    };

    const enableWheel = () => {
      if (this.swiper?.mousewheel?.enabled === false) {
        this.swiper.mousewheel.enable();
      }
    };

    const resetState = () => {
      state.active = false;
      state.released = false;
      state.distance = 0;
      state.prevTranslate = null;
      clearTimeout(state.resetTimer);
      clearTimeout(state.releaseTimer);
      enableWheel();
    };

    const releaseWheel = () => {
      if (state.released) return;
      state.released = true;
      state.active = false;
      if (this.swiper?.mousewheel?.enabled) {
        this.swiper.mousewheel.disable();
      }
      state.releaseTimer = setTimeout(() => {
        resetState();
      }, 1500);
    };

    const onWheel = () => {
      if (state.released) return;
      state.active = true;
      if (state.prevTranslate === null) {
        state.prevTranslate = this.swiper?.getTranslate?.() ?? null;
      }
      clearTimeout(state.resetTimer);
      state.resetTimer = setTimeout(() => {
        resetState();
      }, 700);
    };

    const onSetTranslate = () => {
      if (!state.active || state.released) return;
      const current = this.swiper?.getTranslate?.();
      if (typeof current !== 'number') return;
      if (state.prevTranslate !== null) {
        state.distance += Math.abs(current - state.prevTranslate);
        if (state.distance >= loopDistance * 2) {
          releaseWheel();
        }
      }
      state.prevTranslate = current;
    };

    const onMouseLeave = () => {
      resetState();
    };

    container.addEventListener('wheel', onWheel, { passive: true });
    container.addEventListener('mouseleave', onMouseLeave);
    this.swiper.on('setTranslate', onSetTranslate);

    this._loopWheelHandlers = { container, onWheel, onMouseLeave, onSetTranslate, resetState };
  }

  getLoopDistance() {
    if (!this.swiper) return 0;
    const slides = this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');
    if (slides.length < 2) return 0;
    const first = slides[0];
    const last = slides[slides.length - 1];
    const distance = (last.offsetLeft + last.offsetWidth) - first.offsetLeft;
    return Math.max(0, distance);
  }

  disconnectedCallback() {
    if (this._loopWheelHandlers?.container) {
      const { container, onWheel, onMouseLeave, onSetTranslate, resetState } = this._loopWheelHandlers;
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('mouseleave', onMouseLeave);
      this.swiper?.off?.('setTranslate', onSetTranslate);
      resetState?.();
      this._loopWheelHandlers = null;
    }
    this.swiper?.destroy(true, true);
  }
}

if (!customElements.get('scrolling-gallery')) {
  customElements.define('scrolling-gallery', ScrollingGallery);
}